import React, { useMemo, useState } from "react";
import {
  HiOutlineArrowUturnLeft,
  HiOutlineCheck,
  HiOutlineChevronDown,
  HiOutlineClock,
  HiOutlineCog6Tooth,
  HiOutlineCurrencyDollar,
  HiOutlineExclamationTriangle,
  HiOutlineSquare2Stack,
  HiOutlineUserGroup,
  HiOutlineUsers,
} from "react-icons/hi2";
import Loader from "components/common/Loader/Loader";
import useCareLabels from "hooks/useCareLabels";
import { formatParticipantDisplayName } from "../../TimeSheetDetailsSection/TabSection/NewCodeAllocation/v5BillingUtils";
import {
  formatCodeAllocationShiftRangeLabel,
  resolveTimesheetTimePreference,
} from "../../utils/billingShiftRange";
import { CodeAllocationInvoiceLockAlert } from "../../TimeSheetDetailsSection/components/TimesheetModeBanner";

export const TimesheetCodeAllocationTogglePill = ({ label, children }) => (
  <div className="ca-toggle-pill">
    <span className="ca-toggle-pill-label">{label}</span>
    {children}
  </div>
);

export const TimesheetCodeAllocationSectionCard = ({
  title,
  subtitle,
  icon: Icon,
  headerAction,
  headerBadge,
  children,
  className = "",
}) => (
  <section className={`ca-section-card ${className}`}>
    {(title || headerAction || headerBadge) && (
      <div className="ca-section-header">
        <div className="ca-section-header-left">
          {Icon && (
            <span className="ca-section-icon-wrap" aria-hidden>
              <Icon className="ca-section-icon" />
            </span>
          )}
          <div className="ca-section-header-text">
            {title && <h3 className="ca-section-title">{title}</h3>}
            {subtitle && <p className="ca-section-subtitle">{subtitle}</p>}
          </div>
          {headerBadge}
        </div>
        {headerAction}
      </div>
    )}
    {children}
  </section>
);

export const TimesheetCodeAllocationSettingsColumnHeader = () => (
  <div className="ca-settings-column-header">
    <HiOutlineCog6Tooth className="ca-settings-column-icon" aria-hidden />
    <div>
      <p className="ca-settings-column-title">Shift settings</p>
      <p className="ca-settings-column-hint">Times & service — billing is on the right</p>
    </div>
  </div>
);


export const TimesheetCodeAllocationShiftDurationPill = ({
  duration,
  showRevert = false,
  onRevert,
}) => {
  if (!duration && !showRevert) return null;
  return (
    <div className="ca-shift-duration-wrap">
      {duration ? (
        <div className="ca-shift-duration-pill">
          <HiOutlineClock className="ca-shift-duration-icon" aria-hidden />
          <span className="ca-shift-duration-label">Duration</span>
          <span className="ca-shift-duration-value">{duration}</span>
        </div>
      ) : null}
      {showRevert ? (
        <button
          type="button"
          className="ca-shift-time-revert-btn"
          onClick={onRevert}
        >
          <HiOutlineArrowUturnLeft className="h-3.5 w-3.5" aria-hidden />
          Revert
        </button>
      ) : null}
    </div>
  );
};

export const TimesheetCodeAllocationFundingBadge = () => (
  <span className="ca-funding-badge">Funding blocked</span>
);

export const TimesheetCodeAllocationBillingInsight = ({
  shiftDetails,
  spSettings,
  timeDisplayPreference,
  shiftRangeLabel,
  invoiceEstimate,
  showInvoiceEstimate = true,
}) => {
  const resolvedPreference = useMemo(
    () =>
      timeDisplayPreference ??
      resolveTimesheetTimePreference(shiftDetails, spSettings),
    [timeDisplayPreference, shiftDetails, spSettings]
  );

  const rangeLabel = useMemo(() => {
    if (shiftRangeLabel) return shiftRangeLabel;
    return formatCodeAllocationShiftRangeLabel(
      shiftDetails,
      resolvedPreference
    );
  }, [shiftRangeLabel, shiftDetails, resolvedPreference]);

  return (
  <div className="ca-billing-insight">
    <div className="ca-billing-insight-main">
      <div className="ca-billing-insight-icon-wrap" aria-hidden>
        <HiOutlineClock className="ca-billing-insight-icon" />
      </div>
      <div className="ca-billing-insight-times">
        {rangeLabel && (
          <p className="ca-billing-insight-range">{rangeLabel}</p>
        )}
        <p className="ca-billing-insight-hint">
          Edit the shift window in Shift settings — billing breakdowns may reset
        </p>
      </div>
    </div>
    {showInvoiceEstimate ? (
      <div className="ca-billing-insight-estimate">
        <div className="ca-billing-insight-estimate-icon-wrap" aria-hidden>
          <HiOutlineCurrencyDollar className="ca-billing-insight-estimate-icon" />
        </div>
        <div className="ca-billing-insight-estimate-text">
          <span className="ca-billing-insight-estimate-label">Invoice estimate</span>
          <span className="ca-billing-insight-estimate-value">
            {invoiceEstimate ?? "—"}
          </span>
        </div>
      </div>
    ) : null}
  </div>
  );
};

export const TimesheetCodeAllocationBillingFlags = ({ children }) => (
  <div className="ca-toolbar-card">
    <span className="ca-toolbar-label">Billing options</span>
    <div className="ca-billing-flags">{children}</div>
  </div>
);

export const TimesheetCodeAllocationBreakdownToggleRow = ({
  hint,
  label,
  children,
}) => (
  <div className="ca-breakdown-toggle-card">
    <div className="ca-breakdown-toggle-bar">
      <div className="ca-breakdown-toggle-copy">
        {label && <span className="ca-breakdown-toggle-label">{label}</span>}
        {hint && <span className="ca-breakdown-toggle-hint">{hint}</span>}
      </div>
      <div className="ca-breakdown-toggle-control">{children}</div>
    </div>
  </div>
);

export const TimesheetCodeAllocationRateStrip = ({
  catalogue,
  billRate,
  appliedRate,
}) => {
  if (catalogue == null && billRate == null && appliedRate == null) {
    return null;
  }

  const formatMoney = (val) =>
    val != null && val !== "" ? `$${Number(val).toFixed(2)}` : "—";

  return (
    <div className="ca-rate-strip">
      <div className="ca-rate-strip-cell">
        <span className="ca-rate-strip-label">Catalogue</span>
        <span className="ca-rate-strip-value">{formatMoney(catalogue)}</span>
      </div>
      <div className="ca-rate-strip-cell ca-rate-strip-cell--highlight">
        <span className="ca-rate-strip-label">Bill rate</span>
        <span className="ca-rate-strip-value">{formatMoney(billRate)}</span>
      </div>
      <div className="ca-rate-strip-cell ca-rate-strip-cell--brand">
        <span className="ca-rate-strip-label">Applied /h</span>
        <span className="ca-rate-strip-value ca-rate-strip-value--brand">
          {formatMoney(appliedRate)}
        </span>
      </div>
    </div>
  );
};

export const TimesheetCodeAllocationBreakdownsHeader = ({
  segmentCount,
  durationLabel,
  participantCount = 0,
}) => {
  const { participantLabel, participantLabelPlural } = useCareLabels();
  const participantLower = participantLabel.toLowerCase();
  const participantsLower = participantLabelPlural.toLowerCase();

  return (
  <div className="ca-breakdowns-header">
    <div className="ca-breakdowns-header-icon-wrap" aria-hidden>
      <HiOutlineUserGroup className="ca-breakdowns-header-icon" />
    </div>
    <div className="ca-breakdowns-header-text">
      <p className="ca-breakdowns-title">Billing by {participantLower}</p>
      <p className="ca-breakdowns-subtitle">
        Each card is one {participantLower} — breakdowns inside are their billable time blocks
      </p>
    </div>
    {segmentCount > 0 && (
      <div className="ca-breakdowns-stats">
        {participantCount > 0 && (
          <span className="ca-breakdowns-chip ca-breakdowns-chip--participants">
            {participantCount}{" "}
            {participantCount !== 1 ? participantsLower : participantLower}
          </span>
        )}
        <span className="ca-breakdowns-chip ca-breakdowns-chip--segments">
          {segmentCount} breakdown{segmentCount !== 1 ? "s" : ""}
        </span>
        {durationLabel && (
          <span className="ca-breakdowns-chip ca-breakdowns-chip--duration">
            {durationLabel}
          </span>
        )}
      </div>
    )}
  </div>
  );
};

export const TimesheetCodeAllocationParticipantGroup = ({
  participantIndex,
  totalParticipants,
  initials,
  participantLabel,
  breakdownCount,
  durationLabel,
  ratio,
  defaultExpanded = true,
  children,
}) => {
  const { participantLabel: careParticipantLabel } = useCareLabels();
  const [expanded, setExpanded] = useState(defaultExpanded);
  const showIndex = totalParticipants > 1;
  const displayParticipantLabel =
    formatParticipantDisplayName(participantLabel) || participantLabel;

  return (
    <article
      className={`ca-participant-group${
        expanded ? " is-expanded" : " is-collapsed"
      }`}
      data-participant-index={participantIndex}
    >
      <button
        type="button"
        className="ca-participant-group-header"
        onClick={() => setExpanded((prev) => !prev)}
        aria-expanded={expanded}
      >
        {showIndex && (
          <span className="ca-participant-group-index" aria-hidden>
            {participantIndex}
          </span>
        )}
        <div className="ca-participant-group-avatar" aria-hidden>
          {initials}
        </div>
        <div className="ca-participant-group-text">
          <p className="ca-participant-group-label">
            {showIndex
              ? `${careParticipantLabel} ${participantIndex} of ${totalParticipants}`
              : careParticipantLabel}
          </p>
          <p
            className="ca-participant-group-name"
            title={displayParticipantLabel}
          >
            {displayParticipantLabel}
          </p>
          <div className="ca-participant-group-chips">
            <span className="ca-participant-chip">
              {breakdownCount} breakdown{breakdownCount !== 1 ? "s" : ""}
            </span>
            {durationLabel ? (
              <span className="ca-participant-chip ca-participant-chip--duration">
                {durationLabel}
              </span>
            ) : null}
          </div>
        </div>
        <span
          className={`ca-participant-expand-hint ${
            expanded ? "is-expanded" : ""
          }`}
        >
          {expanded ? "Hide" : "Show"} breakdowns
        </span>
        <HiOutlineChevronDown
          className={`ca-participant-group-chevron ${
            expanded ? "is-open" : ""
          }`}
          aria-hidden
        />
      </button>
      {expanded && (
        <div className="ca-participant-group-breakdowns">
          <p className="ca-participant-segments-label">Time breakdowns</p>
          {children}
        </div>
      )}
    </article>
  );
};

/** @deprecated Use TimesheetCodeAllocationParticipantGroup */
export const TimesheetCodeAllocationParticipantGroupHeader = ({
  initials,
  participantLabel,
  breakdownCount,
  ratio,
}) => (
  <div className="ca-participant-group-header">
    <div className="ca-participant-group-avatar" aria-hidden>
      {initials}
    </div>
    <div className="ca-participant-group-text">
      <p className="ca-participant-group-label">Participant</p>
      <p className="ca-participant-group-name">{participantLabel}</p>
    </div>
    <div className="ca-participant-group-meta">
      {ratio ? (
        <span className="ca-participant-group-ratio">Ratio {ratio}</span>
      ) : null}
      <span className="ca-participant-group-count">
        {breakdownCount} breakdown{breakdownCount !== 1 ? "s" : ""}
      </span>
    </div>
  </div>
);

export const TimesheetCodeAllocationSaveBar = ({
  loading,
  disabled,
  invoiceLocked = false,
  invoiceNumber = null,
}) => (
  <div className="ca-save-bar">
    <div className="ca-save-bar-copy">
      {invoiceLocked ? (
        <CodeAllocationInvoiceLockAlert invoiceNumber={invoiceNumber} compact />
      ) : (
        <p className="ca-save-bar-hint">
          Review each participant&apos;s breakdowns, then save your allocation
        </p>
      )}
    </div>
    <div className="ca-save-bar-actions">
      <button
        type="submit"
        disabled={disabled || loading}
        className="ca-update-btn"
      >
        {loading ? (
          <Loader cssClass="!mr-0 h-4 w-4" isSmallView />
        ) : (
          <HiOutlineCheck className="ca-update-btn-icon" aria-hidden />
        )}
        Update Allocation
      </button>
    </div>
  </div>
);

export const TimesheetCodeAllocationShiftTimePendingBar = ({
  visible = false,
  hasMultiSegmentSplits = false,
  onApply,
  onCancel,
}) => {
  if (!visible) return null;

  return (
    <div className="ca-shift-time-pending-bar" role="status">
      <div className="ca-shift-time-pending-bar-content">
        <HiOutlineExclamationTriangle
          className="ca-shift-time-pending-bar-icon"
          aria-hidden
        />
        <p className="ca-shift-time-pending-bar-text">
          If you change the shift time, worker payroll times on the timesheet will be
          updated as well. Each participant&apos;s billing breakdown will be reset to
          the full shift window
          {hasMultiSegmentSplits
            ? ", and any participant with more than one breakdown will have the extra breakdowns removed"
            : ""}
          . Press <strong>Change time</strong> to apply, or <strong>Cancel</strong> to
          keep the current window.
        </p>
      </div>
      <div className="ca-shift-time-pending-bar-actions">
        <button
          type="button"
          className="ca-shift-time-pending-bar-btn ca-shift-time-pending-bar-btn--cancel"
          onClick={onCancel}
        >
          Cancel
        </button>
        <button
          type="button"
          className="ca-shift-time-pending-bar-btn ca-shift-time-pending-bar-btn--apply"
          onClick={onApply}
        >
          Change time
        </button>
      </div>
    </div>
  );
};

export const TimesheetCodeAllocationUpdateWarningModal = ({
  visible = false,
  loading = false,
  onCancel,
  onConfirm,
}) => {
  if (!visible) return null;

  return (
    <div
      className="ca-shift-time-modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="ca-update-warning-title"
    >
      <div className="ca-shift-time-modal">
        <span className="ca-shift-time-modal-icon">
          <HiOutlineExclamationTriangle className="h-6 w-6" aria-hidden />
        </span>
        <h3 id="ca-update-warning-title" className="ca-shift-time-modal-title">
          Shift time was changed
        </h3>
        <p className="ca-shift-time-modal-body">
          You changed this shift&apos;s time, so the worker payroll times and each
          participant&apos;s billing breakdown were reset to the new window.
          Updating the allocation will save these changes. Do you want to
          continue?
        </p>
        <div className="ca-shift-time-modal-actions">
          <button
            type="button"
            className="ca-shift-time-modal-btn ca-shift-time-modal-btn--cancel"
            onClick={onCancel}
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="button"
            className="ca-shift-time-modal-btn ca-shift-time-modal-btn--proceed"
            onClick={onConfirm}
            disabled={loading}
          >
            Proceed
          </button>
        </div>
      </div>
    </div>
  );
};

export const TimesheetCodeAllocationRatioBadge = ({ ratio }) => (
  <span className="ca-ratio-badge">
    <HiOutlineUsers className="h-3 w-3 shrink-0" aria-hidden />
    Ratio {ratio || "1:1"}
  </span>
);

export const TimesheetCodeAllocationBreakdownIcon = () => (
  <HiOutlineSquare2Stack className="ca-breakdown-header-icon" aria-hidden />
);
